import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { CalendarCheck, Plus, User, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AttendancePage() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [markOpen, setMarkOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ meeting_id: "", date: "", user_ids: [], status: "present" });
  const [searchQuery, setSearchQuery] = useState("");
  // Round 38 — course-first attendance workflow (Task 8).
  const [courseAttOpen, setCourseAttOpen] = useState(false);
  const [coursesList, setCoursesList] = useState([]);
  const [includeFreeCourses, setIncludeFreeCourses] = useState(false); // Task 7 filter
  const [courseAttForm, setCourseAttForm] = useState({ course_id: "", date: new Date().toISOString().slice(0, 10), note: "" });
  const [roster, setRoster] = useState({ students: [], summary: null });
  const [rosterLoading, setRosterLoading] = useState(false);

  const isAdmin = ["super_admin", "admin", "instructor"].includes(user?.role);
  const userId = user?.id || user?._id;

  useEffect(() => {
    api.get("/attendance").then(r => { setRecords(r.data); setLoading(false); }).catch(() => setLoading(false));
    if (isAdmin) {
      api.get("/users/directory").then(r => setStudents(r.data.filter(u => u.role === "student"))).catch(() => {});
      api.get("/meetings").then(r => setMeetings(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  // Task 7 — reload course list whenever the filter changes.
  useEffect(() => {
    if (!isAdmin) return;
    const params = includeFreeCourses ? { include_free: true } : { include_free: false };
    api.get("/courses", { params }).then(r => setCoursesList(r.data || [])).catch(() => setCoursesList([]));
  }, [isAdmin, includeFreeCourses]);

  // Load the roster whenever the admin picks a course + date.
  useEffect(() => {
    if (!courseAttOpen || !courseAttForm.course_id) return;
    setRosterLoading(true);
    api.get(`/courses/${courseAttForm.course_id}/attendance/roster`, { params: { date: courseAttForm.date } })
      .then(r => setRoster({ students: r.data?.students || [], summary: r.data?.summary || null }))
      .catch(() => setRoster({ students: [], summary: null }))
      .finally(() => setRosterLoading(false));
  }, [courseAttOpen, courseAttForm.course_id, courseAttForm.date]);

  const remark = async (student_ids, status) => {
    if (!courseAttForm.course_id || student_ids.length === 0) return;
    try {
      await api.post(`/courses/${courseAttForm.course_id}/attendance/remark`, {
        date: courseAttForm.date, student_ids, status, note: courseAttForm.note,
      });
      // Refresh roster.
      const { data } = await api.get(`/courses/${courseAttForm.course_id}/attendance/roster`, { params: { date: courseAttForm.date } });
      setRoster({ students: data?.students || [], summary: data?.summary || null });
      const { data: rec } = await api.get("/attendance");
      setRecords(rec);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update attendance");
    }
  };

  const markAllPresent = async () => {
    const ids = roster.students.filter(s => s.status !== "present").map(s => s.user_id);
    if (ids.length === 0) { toast.info("Everyone already marked present"); return; }
    await remark(ids, "present");
    toast.success(`Marked ${ids.length} student(s) present`);
  };

  const markCourseAttendance = async () => {
    // Legacy submit path — kept for backwards-compat when admin used the older CTA.
    const ids = roster.students.filter(s => s.status !== "present").map(s => s.user_id);
    if (ids.length === 0) { toast.info("Everyone already marked present"); return; }
    await remark(ids, "present");
    setCourseAttOpen(false);
    setCourseAttForm({ course_id: "", date: new Date().toISOString().slice(0, 10), note: "" });
    setRoster({ students: [], summary: null });
  };

  useEffect(() => {
    const sid = isAdmin ? selectedStudent : userId;
    if (sid) {
      api.get(`/attendance/summary/${sid}`).then(r => setSummary(r.data)).catch(() => setSummary(null));
    }
  }, [selectedStudent, isAdmin, userId]);

  const toggleUser = (id) => {
    setForm(prev => ({
      ...prev,
      user_ids: prev.user_ids.includes(id) ? prev.user_ids.filter(u => u !== id) : [...prev.user_ids, id]
    }));
  };

  const markAttendance = async () => {
    if (form.user_ids.length === 0) { toast.error("Select at least one student"); return; }
    try {
      await api.post("/attendance", form);
      toast.success("Attendance marked!");
      setMarkOpen(false);
      setForm({ meeting_id: "", date: "", user_ids: [], status: "present" });
      const { data } = await api.get("/attendance");
      setRecords(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to mark"); }
  };

  const filteredStudents = students.filter(u =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statusIcon = (status) => {
    if (status === "present") return <CheckCircle size={16} className="text-green-600" />;
    if (status === "absent") return <XCircle size={16} className="text-red-500" />;
    return <Clock size={16} className="text-amber-500" />;
  };

  const statusColor = (status) => {
    if (status === "present") return "bg-green-100 text-green-800";
    if (status === "absent") return "bg-red-100 text-red-700";
    return "bg-amber-100 text-amber-800";
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="attendance-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Records</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Attendance
            </h1>
          </div>
          {isAdmin && (
            <div className="flex gap-2 flex-wrap">
            <Dialog open={markOpen} onOpenChange={setMarkOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="mark-attendance-btn">
                  <Plus size={16} weight="bold" /> Mark by Meeting
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md rounded-none max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Mark Attendance</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  <div>
                    <Label className="text-sm">Meeting (optional)</Label>
                    <Select value={form.meeting_id} onValueChange={v => setForm({ ...form, meeting_id: v })}>
                      <SelectTrigger className="mt-1 rounded-none"><SelectValue placeholder="Select meeting" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No meeting (manual)</SelectItem>
                        {meetings.map(m => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Date</Label>
                    <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-none" />
                  </div>
                  <div>
                    <Label className="text-sm">Status</Label>
                    <div className="flex gap-1 mt-1 bg-slate-100 p-1">
                      {["present", "absent", "late"].map(s => (
                        <button key={s} onClick={() => setForm({ ...form, status: s })}
                          className={`flex-1 py-2 text-xs font-medium capitalize transition-colors ${form.status === s ? (s === "present" ? "bg-green-700 text-white" : s === "absent" ? "bg-red-600 text-white" : "bg-amber-600 text-white") : "text-slate-600 hover:bg-slate-200"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Students ({form.user_ids.length} selected)</Label>
                    <Input placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="mt-1 rounded-none mb-2" />
                    <div className="max-h-40 overflow-y-auto border border-slate-200 p-1 space-y-0.5">
                      {filteredStudents.map(u => (
                        <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
                          <input type="checkbox" checked={form.user_ids.includes(u.id)} onChange={() => toggleUser(u.id)} className="accent-blue-800" />
                          <span className="truncate">{u.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button onClick={markAttendance} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="submit-attendance-btn">
                    <CalendarCheck size={16} /> Mark Attendance
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Round 38 — course-first attendance (Task 8) with auto roster + Mark All Present + Unmark */}
            <Dialog open={courseAttOpen} onOpenChange={setCourseAttOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-none gap-2" data-testid="mark-course-attendance-btn">
                  <CalendarCheck size={16} weight="bold" /> Mark by Course
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Course attendance</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-slate-500 mt-1">
                  Pick a course to see every enrolled student. Mark all present in one click, flip individual students to absent, or unmark a wrong entry — every change updates the transcript hooks.
                </p>
                <div className="space-y-3 mt-3">
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[220px]">
                      <Label className="text-sm">Course</Label>
                      <Select value={courseAttForm.course_id} onValueChange={v => setCourseAttForm({ ...courseAttForm, course_id: v })}>
                        <SelectTrigger className="mt-1 rounded-none" data-testid="course-att-course-select"><SelectValue placeholder="Pick a course…" /></SelectTrigger>
                        <SelectContent>
                          {coursesList.map(c => <SelectItem key={c.id} value={c.id}>{c.code ? `${c.code} · ` : ""}{c.title}{c.is_free ? " (free)" : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-40">
                      <Label className="text-sm">Date</Label>
                      <Input type="date" value={courseAttForm.date} onChange={e => setCourseAttForm({ ...courseAttForm, date: e.target.value })} className="mt-1 rounded-none" data-testid="course-att-date" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={includeFreeCourses} onChange={e => setIncludeFreeCourses(e.target.checked)} data-testid="course-att-include-free" />
                    Include free courses in the list (paid programme courses only by default)
                  </label>

                  {courseAttForm.course_id && (
                    <>
                      {/* Summary chips */}
                      {roster.summary && (
                        <div className="flex flex-wrap gap-2 mt-2" data-testid="course-att-summary">
                          <Badge className="rounded-none bg-slate-100 text-slate-800">Total {roster.summary.total}</Badge>
                          <Badge className="rounded-none bg-green-100 text-green-800">Present {roster.summary.present}</Badge>
                          <Badge className="rounded-none bg-red-100 text-red-700">Absent {roster.summary.absent}</Badge>
                          <Badge className="rounded-none bg-amber-100 text-amber-800">Unmarked {roster.summary.unmarked}</Badge>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <Button onClick={markAllPresent} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none text-xs gap-1" data-testid="course-att-mark-all-present">
                          <CheckCircle size={14} /> Mark All Present
                        </Button>
                      </div>

                      <div className="border border-slate-200">
                        <Input placeholder="Search students by name or email…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="rounded-none border-0 border-b" />
                        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100" data-testid="course-att-roster">
                          {rosterLoading ? (
                            <p className="p-4 text-sm text-slate-500">Loading roster…</p>
                          ) : roster.students.length === 0 ? (
                            <p className="p-4 text-sm text-slate-500">No enrolled students on this course yet.</p>
                          ) : (
                            roster.students
                              .filter(s => !searchQuery || (s.name + s.email + (s.admission_number || "")).toLowerCase().includes(searchQuery.toLowerCase()))
                              .map(s => (
                                <div key={s.user_id} className="p-3 flex items-center gap-3" data-testid={`roster-row-${s.user_id}`}>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-900 truncate">{s.name || "—"}</p>
                                    <p className="text-[11px] text-slate-500 truncate">{s.email}{s.admission_number ? ` · ${s.admission_number}` : ""}</p>
                                  </div>
                                  <Badge className={`rounded-none text-[10px] ${s.status === "present" ? "bg-green-100 text-green-800" : s.status === "absent" ? "bg-red-100 text-red-700" : "bg-slate-200 text-slate-700"}`} data-testid={`roster-status-${s.user_id}`}>
                                    {s.status}
                                  </Badge>
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => remark([s.user_id], "present")}
                                      className="px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[11px] rounded-none"
                                      data-testid={`roster-present-${s.user_id}`}
                                    >Present</button>
                                    <button
                                      onClick={() => remark([s.user_id], "absent")}
                                      className="px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 text-[11px] rounded-none"
                                      data-testid={`roster-absent-${s.user_id}`}
                                    >Absent</button>
                                    {s.status !== "unmarked" && (
                                      <button
                                        onClick={() => remark([s.user_id], "unmarked")}
                                        className="px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50 text-[11px] rounded-none"
                                        data-testid={`roster-unmark-${s.user_id}`}
                                      >Unmark</button>
                                    )}
                                  </div>
                                </div>
                              ))
                          )}
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm">Note (optional)</Label>
                        <Input value={courseAttForm.note} onChange={e => setCourseAttForm({ ...courseAttForm, note: e.target.value })} className="mt-1 rounded-none" placeholder="e.g. Week 3 live class" />
                      </div>
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        {(isAdmin ? selectedStudent : true) && summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Total Sessions", value: summary.total_sessions, color: "text-slate-800" },
              { label: "Present", value: summary.present, color: "text-green-700" },
              { label: "Absent", value: summary.absent, color: "text-red-600" },
              { label: "Attendance Rate", value: `${summary.attendance_rate}%`, color: "text-blue-800" },
            ].map((s, i) => (
              <Card key={i} className="border border-slate-200 rounded-none">
                <CardContent className="p-4 text-center">
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {isAdmin && (
          <div className="mb-6 max-w-xs">
            <Label className="text-sm text-slate-600">View Student Summary</Label>
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger className="mt-1 rounded-none"><SelectValue placeholder="Select student" /></SelectTrigger>
              <SelectContent>
                {students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <Separator className="mb-6" />

        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-slate-100 animate-pulse" />)}</div>
        ) : records.length === 0 ? (
          <div className="text-center py-16">
            <CalendarCheck size={48} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No attendance records found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attendance-table">
              <thead>
                <tr className="bg-slate-950 text-white">
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="text-left px-3 py-3 font-medium">Meeting</th>
                  <th className="text-center px-3 py-3 font-medium">Status</th>
                  <th className="text-center px-3 py-3 font-medium">Marked By</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => (
                  <tr key={r.id || i} className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                    <td className="px-4 py-3 text-slate-900">{r.date}</td>
                    <td className="px-3 py-3 text-slate-600">{r.meeting_id ? r.meeting_id.slice(0, 8) + "..." : "Manual"}</td>
                    <td className="text-center px-3 py-3">
                      <Badge className={`rounded-none text-xs capitalize ${statusColor(r.status)}`}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="text-center px-3 py-3 text-slate-500 text-xs">{r.marked_by === "system" ? "Auto" : "Manual"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
